import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { loginOutApi } from '@/api/login'
import { useTagsViewStore } from './tagsView'

interface UserState {
  userInfo?: UserType
  /** 调用 /admin/login 时表单填写的用户名（与请求体 name 一致），用于顶栏展示 */
  loginUsername: string
  tokenKey: string
  token: string
  tokenType: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  /**
   * 谷歌验证有效期（毫秒 timestamp）。在 useGoogleAuth() 验证成功后 setOtpUnlockUntil(now + 1h)；
   * 期内调 requireGoogleAuth() 直接 resolve 跳过弹窗。0 表示已过期/未验证。
   */
  otpUnlockUntil: number
  /** 手动锁屏：true 时全屏遮罩 + 必须输 Google 验证码解锁；解锁后顺手延长 otpUnlockUntil */
  locked: boolean
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      loginUsername: '',
      tokenKey: 'Authorization',
      token: '',
      tokenType: '',
      roleRouters: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined,
      otpUnlockUntil: 0,
      locked: false
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.tokenType ? `${this.tokenType} ${this.token}` : this.token
    },
    getTokenType(): string {
      return this.tokenType
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getLoginUsername(): string {
      return this.loginUsername
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getOtpUnlockUntil(): number {
      return this.otpUnlockUntil
    },
    /** 当前 OTP 是否仍在有效期内（true 时 useGoogleAuth 跳过弹窗） */
    isOtpUnlocked(): boolean {
      return this.otpUnlockUntil > Date.now()
    },
    getLocked(): boolean {
      return this.locked
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setTokenType(tokenType: string) {
      this.tokenType = tokenType
    },
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },
    setLoginUsername(username: string) {
      this.loginUsername = (username ?? '').trim()
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          const res = await loginOutApi().catch(() => {})
          if (res) {
            this.reset()
          }
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setLoginUsername('')
      this.setRoleRouters([])
      // 退出登录时同时清掉 OTP 有效期和锁屏状态，否则 persist 让这两个 flag 跟着新一次登录残留：
      //  - otpUnlockUntil 残留：新用户登录后 1h 内直接 autoFill，跳过 OTP
      //  - locked 残留：用户重登后第一时间被锁屏拦住，且若 token 还没拿到 verifyOtp 会 401 卡死
      this.otpUnlockUntil = 0
      this.locked = false
      // 硬刷新到登录页：保证发版后退出 → 重登 时浏览器去拉最新的 index.html，
      // 而不是 SPA 内部 router.replace（那样仍然用老缓存）。
      // location.replace 不留 history，按返回键不会回到登录前的内部页面
      const base = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '')
      window.location.replace(`${base}/#/login?_t=${Date.now()}`)
      window.location.reload()
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    /** Google 验证通过后调用：延长有效期到 now + ms（默认 1 小时） */
    setOtpUnlockUntil(ttlMs: number = 60 * 60 * 1000) {
      this.otpUnlockUntil = Date.now() + Math.max(0, ttlMs)
    },
    /** 归零：清掉有效期，下次高危必须重新输 OTP */
    clearOtpUnlock() {
      this.otpUnlockUntil = 0
    },
    /** 锁屏：用户主动触发；遮罩组件读 locked 显示。注意：锁屏不清 OTP 有效期，解锁后业务可继续 */
    lock() {
      this.locked = true
    },
    unlock() {
      this.locked = false
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
